'use client';

import { useId } from 'react';
import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useTranslations } from 'next-intl';

import { Orb } from '@/components/orb';
import { Link } from '@/i18n/navigation';
import { dur, spring, springFade, stagger } from '@/lib/motion';

/**
 * Ürün sayfalarının ortak açılışı — /features/* (14 Eki 2026).
 *
 * İlk sürümü `agent-hero.tsx`ti; dört ürün sayfası aynı kabı kullansın diye
 * buraya çıkarıldı. Kap, küre, maske ve sınıflar AYNI (`.agent-hero-*`,
 * globals.css → "AI Ajan hero — küre zemin"): dört sayfa aynı malzemeden
 * okunuyor, değişen yalnız rozet ikonu ve metin. Sayfayı farklı kılan şey
 * açılış değil, hemen altındaki gösterim bölümü.
 *
 * ── Zemin anasayfayla AYNI (kullanıcı kararı, 10 Ağu 2026) ────────────────
 *
 * Sayfa `.dark-block` ile açılıyor: aynı teal→lacivert rampa, aynı ızgara,
 * aynı dikey hatlar, header aynı biçimde üst kenara yapışık başlıyor.
 * Anasayfada ışık tabandan yükselen dalga alanı (`gradient-waves.tsx`),
 * burada küre (`orb.tsx`).
 *
 * Tipografi ödünç: başlık, açıklama, butonlar ve not satırı anasayfanın
 * hero sınıflarını (`.new-hero-*`) kullanıyor — aynı ölçek tek yerde. O
 * sınıflar vendor CSS'ine `[data-v-…]` seçicisiyle bağlı, nitelik markup'ta
 * tekrarlanıyor (gerekçe: components/hero.tsx).
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `animate` ile doğrudan oynuyor. Sıra
 * okuma sırası: rozet → başlık → cümle → butonlar → not. Blur yalnız
 * başlıkta — göz oraya odaklanıyor. Küre metinle birlikte, 0.94 ölçekten:
 * "zaten oradaydı, ışığı açıldı". Kürenin kendi döngüsü ve azaltılmış hareket
 * `orb.tsx`te.
 *
 * Metin anahtarları: `<ns>.eyebrow`, `titleLine1`, `titleLine2`,
 * `description`, `primaryCta`, `secondaryCta`, `note`.
 */

const V = 'd0b8df6f';

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

const orbV: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.soft, dur.slow) },
};

export function ProductHero({
  ns,
  icon,
  visual,
}: {
  ns: string;
  icon: ReactNode;
  /** Kopyanın altındaki ürün sahnesi (22 Eki 2026) — `components/features/*-stage.tsx`. */
  visual?: ReactNode;
}) {
  const t = useTranslations(ns);
  const uid = useId();

  return (
    <section className="agent-hero" aria-labelledby={`${uid}-title`}>
      {/* Header `position: fixed`, akışta yer kaplamıyor — payı burada. */}
      <div className="agent-hero-space" aria-hidden="true" />

      <div className="agent-hero-body">
        {/* Küre sütunun DIŞINDA ve ALTINDA: içerik sütununun dikey hatları
            kürenin üstünde kalıyor (hatlar `.agent-hero-frame`in kenarları). */}
        <motion.div className="agent-hero-orb" variants={orbV} initial="hidden" animate="show">
          <Orb className="agent-hero-orb-canvas" />
        </motion.div>

        <div className="grid-12-container">
          <div className="grid-item-2fr" />

          <div className="grid-item-8fr">
            <div className={`agent-hero-frame${visual ? ' agent-hero-frame--visual' : ''}`}>
              <motion.div
                className="agent-hero-copy"
                variants={sceneV}
                initial="hidden"
                animate="show"
              >
                <motion.p className="agent-hero-eyebrow" variants={itemV}>
                  {icon}
                  {t('eyebrow')}
                </motion.p>

                <motion.h1
                  className="new-hero-title"
                  data-v-d0b8df6f={V}
                  id={`${uid}-title`}
                  variants={titleV}
                >
                  <span
                    className="new-hero-title-line new-hero-title-line--lead"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine1')}
                  </span>
                  <span
                    className="new-hero-title-line new-hero-title-line--accent"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine2')}
                  </span>
                </motion.h1>

                <motion.p
                  className="new-hero-description"
                  data-v-d0b8df6f={V}
                  variants={itemV}
                >
                  {t('description')}
                </motion.p>

                <motion.div className="new-hero-buttons" data-v-d0b8df6f={V} variants={itemV}>
                  <Link href="/register" className="new-button-primary" data-v-d0b8df6f={V}>
                    {t('primaryCta')}
                  </Link>
                  <Link href="/pricing" className="new-button-outline" data-v-d0b8df6f={V}>
                    {t('secondaryCta')}
                  </Link>
                </motion.div>

                <motion.p className="new-hero-trial-text" data-v-d0b8df6f={V} variants={itemV}>
                  {t('note')}
                </motion.p>
              </motion.div>

              {visual ? (
                <motion.div
                  className="fx-hero-visual"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={springFade(spring.soft, dur.slow)}
                >
                  {visual}
                </motion.div>
              ) : null}
            </div>
          </div>

          <div className="grid-item-2fr" />
        </div>
      </div>
    </section>
  );
}
